import React, { useState, useEffect } from 'react';
import SignInDialog from './SignInDialog';
import GoogleFormModal from './GoogleFormModal';
import { formatVND } from '../utils/priceFormatter';
import { getCourseIntroVideo } from '../data/courseVideos';

interface Course {
  id: string;
  name: string;
  code: string;
  description: string;
  color: string;
  videoCount: number;
  type: 'video' | 'zoom-recorded';
  instructor: string;
  duration: string;
  level: string;
  price: string;
  discountPrice?: string;
  endDiscountDate?: string;
  rating: number;
  students: number;
  readyToBuy?: boolean;
  whatYoullLearn?: string[];
  additionalDescription?: string;
  courseInfoBox?: {
    title: string;
    description: string;
    links?: Array<{
      text: string;
      url: string;
    }>;
  };
  instructorProfile?: {
    title: string;
    experience: string;
    education: string;
    certification: string;
    linkedin: string;
    github: string;
    image?: string;
  };
}

interface CourseDetailProps {
  course: Course;
  onBack: () => void;
  onGoToCourse?: (courseId: string) => void;
  hasAccess?: boolean;
  user?: {
    name: string;
    email: string;
    picture?: string;
  } | null;
  onSignIn?: () => void;
}

// Countdown Timer Component
const CountdownTimer: React.FC<{ endDate: string; color: string; compact?: boolean }> = ({ endDate, color, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endDate).getTime();
      const now = new Date().getTime();
      const difference = end - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  if (!timeLeft) return null;

  // Show all time units on both mobile and desktop
  const timeUnits = [
    { value: timeLeft.days, label: 'Days', short: 'D' },
    { value: timeLeft.hours, label: 'Hours', short: 'H' },
    { value: timeLeft.minutes, label: 'Minutes', short: 'M' },
    { value: timeLeft.seconds, label: 'Seconds', short: 'S' }
  ];

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: compact && isMobile ? 'space-between' : 'flex-start',
      gap: compact ? (isMobile ? '8px' : '6px') : '8px',
      padding: isMobile && compact ? '10px 12px' : '12px 16px',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      borderRadius: '12px',
      border: `1.5px solid ${color}30`,
      marginBottom: compact ? '0' : '16px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background pulse */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: `linear-gradient(90deg, transparent, ${color}20, transparent)`,
        animation: 'shimmer 3s infinite',
        pointerEvents: 'none'
      }} />
      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile && compact ? '4px' : '6px',
        position: 'relative',
        zIndex: 1,
        flexShrink: 0
      }}>
        <span style={{ fontSize: compact ? (isMobile ? '14px' : '16px') : '18px' }}>⏰</span>
        <span style={{
          fontSize: compact ? (isMobile ? '9px' : '10px') : '12px',
          fontWeight: '600',
          color: '#64748b',
          letterSpacing: '0.3px',
          whiteSpace: 'nowrap'
        }}>
          {isMobile && compact ? 'Ends:' : 'Sale ends:'}
        </span>
      </div>

      <div style={{
        display: 'flex',
        gap: compact ? (isMobile ? '4px' : '4px') : '8px',
        position: 'relative',
        zIndex: 1
      }}>
        {timeUnits.map((unit, index) => (
          <div key={index} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: compact ? (isMobile ? '29px' : '32px') : '48px',
            padding: compact ? (isMobile ? '4px 5px' : '4px 6px') : '6px 10px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: `0 2px 8px ${color}20`,
            border: `1px solid ${color}20`,
            flexShrink: 0
          }}>
            <span style={{
              fontSize: compact ? (isMobile ? '13px' : '14px') : '18px',
              fontWeight: '700',
              color: color,
              lineHeight: '1.2'
            }}>
              {String(unit.value).padStart(2, '0')}
            </span>
            <span style={{
              fontSize: compact ? (isMobile ? '6.5px' : '7px') : '9px',
              fontWeight: '600',
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginTop: '2px'
            }}>
              {unit.short}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to check if discount is still valid
const isDiscountValid = (course: Course): boolean => {
  if (!course.discountPrice || !course.endDiscountDate) {
    return false;
  }
  const endDate = new Date(course.endDiscountDate).getTime();
  const now = new Date().getTime();
  return endDate > now;
};

const CourseDetail: React.FC<CourseDetailProps> = ({ course, onBack, onGoToCourse, hasAccess = false, user, onSignIn }) => {
  const [isSignInDialogOpen, setIsSignInDialogOpen] = useState(false);
  const [isGoogleFormOpen, setIsGoogleFormOpen] = useState(false);
  const isVideoCourse = course.type === 'video';

  // Scroll to top when component mounts or course changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [course.id]);

  const handleSignInClick = () => {
    setIsSignInDialogOpen(true);
  };

  const handleSignIn = async () => {
    if (onSignIn) {
      await onSignIn();
    }
  };

  const handleBuyCourse = () => {
    if (user) {
      // User is authenticated, open Google Form modal
      setIsGoogleFormOpen(true);
    } else {
      // User not authenticated, show login dialog
      handleSignInClick();
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        .course-content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }
        
        .course-sidebar {
          position: sticky;
          top: 20px;
        }
        
        .course-hero-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .course-hero-price {
          text-align: right;
        }
        
        @media (max-width: 768px) {
          .course-content-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .course-sidebar {
            position: static;
          }
          
          .course-hero-content {
            flex-direction: column;
            gap: 24px;
          }
          
          .course-hero-price {
            text-align: left;
            width: 100%;
          }
          
          .course-hero-price > div {
            justify-content: flex-start !important;
          }
        }
      `}</style>
      {/* Header with back button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '32px',
        paddingBottom: '20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: '#4b5563',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.color = '#111827';
            e.currentTarget.style.transform = 'translateX(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.color = '#4b5563';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Courses
        </button>
      </div>

      {/* Modern Course Hero Section */}
      <div style={{
        position: 'relative',
        borderRadius: '32px',
        overflow: 'hidden',
        marginBottom: '40px',
        boxShadow: `0 20px 40px -10px ${course.color}30`
      }}>
        {/* Immersive Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${course.color} 0%, #1e293b 100%)`,
          zIndex: 0
        }} />

        {/* Decorative Patterns */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          zIndex: 1
        }} />

        <div style={{
          position: 'relative',
          zIndex: 2,
          padding: 'clamp(32px, 5vw, 64px)',
          color: 'white',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '32px'
        }}>
          {/* Badges */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(8px)',
              padding: '8px 16px',
              borderRadius: '100px',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {course.code}
            </span>
            <span style={{
              background: isVideoCourse ? 'rgba(56, 189, 248, 0.2)' : 'rgba(74, 222, 128, 0.2)',
              backdropFilter: 'blur(8px)',
              padding: '8px 16px',
              borderRadius: '100px',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              color: isVideoCourse ? '#bae6fd' : '#bbf7d0',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {isVideoCourse ? '📹 Video Course' : '📺 Live Recording'}
            </span>
          </div>

          {/* Title & Description */}
          <div>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              lineHeight: '1.1',
              marginBottom: '24px',
              letterSpacing: '-0.02em',
              textShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}>
              {course.name}
            </h1>
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
              lineHeight: '1.6',
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '800px',
              marginBottom: '32px'
            }}>
              {course.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="hero-stats-grid" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            marginTop: 'auto'
          }}>
            {/* Instructor - Clickable */}
            <div
              className="hero-stat-card clickable"
              onClick={() => {
                document.getElementById('instructor-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(12px)',
                padding: '12px 20px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <span style={{ fontSize: '24px' }}>👨‍🏫</span>
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px' }}>{course.instructor}</div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '500' }}>Instructor</div>
              </div>
            </div>

            {/* Other Stats */}
            {[
              { icon: '⏱️', label: course.duration, sub: 'Duration' },
              { icon: '📹', label: `${course.videoCount} videos`, sub: 'Content' },
              { icon: '📊', label: course.level, sub: 'Level' }
            ].map((stat, idx) => (
              <div key={idx} className="hero-stat-card" style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(12px)',
                padding: '12px 20px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'transform 0.2s ease',
                cursor: 'default'
              }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <span style={{ fontSize: '24px' }}>{stat.icon}</span>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px' }}>{stat.label}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '500' }}>{stat.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="course-content-grid" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 380px',
        gap: '40px',
        alignItems: 'start'
      }}>
        {/* Left Column: Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

          {/* What You'll Learn */}
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '40px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ color: course.color }}>🎯</span> What you'll learn
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {(course.whatYoullLearn || []).map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '12px',
                  background: '#f8fafc',
                  transition: 'background 0.2s ease'
                }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: `${course.color}20`,
                    color: course.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </div>
                  <span style={{
                    fontSize: '15px',
                    color: '#475569',
                    lineHeight: '1.5',
                    fontWeight: '500'
                  }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Course Description */}
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '40px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Description
            </h2>
            <div style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#475569'
            }}>
              <p style={{ marginBottom: '20px' }}>{course.description}</p>
              {course.additionalDescription && (
                <p style={{ marginBottom: '0' }}>{course.additionalDescription}</p>
              )}
            </div>

            {course.courseInfoBox && (
              <div style={{
                marginTop: '32px',
                background: `linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)`,
                borderRadius: '20px',
                padding: '32px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#0284c7',
                  marginBottom: '12px'
                }}>
                  {course.courseInfoBox.title}
                </h3>
                <p style={{
                  color: '#0369a1',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {course.courseInfoBox.description}
                </p>
                {course.courseInfoBox.links && (
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {course.courseInfoBox.links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 20px',
                          background: 'white',
                          color: '#0284c7',
                          borderRadius: '12px',
                          fontWeight: '600',
                          fontSize: '14px',
                          textDecoration: 'none',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        {link.text} ↗
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructor Profile */}
          {course.instructorProfile && (
            <div id="instructor-section" style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <h2 className="instructor-title" style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '32px'
              }}>
                Meet your Instructor
              </h2>

              <div className="instructor-content" style={{ display: 'flex', gap: '32px', alignItems: 'start', flexWrap: 'wrap' }}>
                <div className="instructor-avatar" style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '24px',
                  background: course.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  fontWeight: '700',
                  color: 'white',
                  boxShadow: `0 20px 40px -10px ${course.color}40`,
                  backgroundImage: course.instructorProfile.image ? `url(${course.instructorProfile.image})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0
                }}>
                  {!course.instructorProfile.image && course.instructor.charAt(0)}
                </div>

                <div className="instructor-info" style={{ flex: 1, minWidth: '280px' }}>
                  <h3 className="instructor-name" style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                    {course.instructor}
                  </h3>
                  <p className="instructor-role" style={{ color: course.color, fontWeight: '600', marginBottom: '16px' }}>
                    {course.instructorProfile.title}
                  </p>

                  <div className="instructor-credentials" style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
                    {[
                      { icon: '💼', text: course.instructorProfile.experience },
                      { icon: '🎓', text: course.instructorProfile.education },
                      { icon: '🏆', text: course.instructorProfile.certification }
                    ].map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748b' }}>
                        <span>{item.icon}</span>
                        <span style={{ fontWeight: '500' }}>{item.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="instructor-links" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <a href={course.instructorProfile.linkedin} target="_blank" rel="noreferrer" style={{
                      padding: '10px 20px',
                      background: '#0077b5',
                      color: 'white',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      LinkedIn
                    </a>
                    <a href={course.instructorProfile.github} target="_blank" rel="noreferrer" style={{
                      padding: '10px 20px',
                      background: '#1e293b',
                      color: 'white',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      GitHub
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Sticky Sidebar */}
        <div style={{ position: 'sticky', top: '100px' }}>
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            {/* Video Preview */}
            {(() => {
              const introVideo = getCourseIntroVideo(course.id);

              if (introVideo && introVideo.vimeoId) {
                return (
                  <div style={{
                    aspectRatio: '16/9',
                    borderRadius: '16px',
                    marginBottom: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}>
                    <iframe
                      src={`https://player.vimeo.com/video/${introVideo.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      title={introVideo.title}
                    />
                  </div>
                );
              }

              // Fallback to placeholder if no intro video
              return (
                <div style={{
                  aspectRatio: '16/9',
                  background: '#f1f5f9',
                  borderRadius: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                    zIndex: 2
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill={course.color}>
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(45deg, ${course.color}20, transparent)`,
                    zIndex: 1
                  }} />
                </div>
              );
            })()}

            {/* Price Section */}
            <div style={{ marginBottom: '24px' }}>
              {isDiscountValid(course) ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                    {formatVND(course.discountPrice)}
                  </span>
                  <span style={{ fontSize: '1.25rem', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '500' }}>
                    {formatVND(course.price)}
                  </span>
                  <span style={{
                    background: '#fee2e2',
                    color: '#ef4444',
                    padding: '4px 12px',
                    borderRadius: '100px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    SAVE {Math.round((1 - parseInt(course.discountPrice!) / parseInt(course.price)) * 100)}%
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                  {formatVND(course.price)}
                </span>
              )}
            </div>

            {/* Countdown Timer */}
            {isDiscountValid(course) && (
              <div style={{ marginBottom: '24px' }}>
                <CountdownTimer endDate={course.endDiscountDate!} color={course.color} compact />
              </div>
            )}

            {/* CTA Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {hasAccess ? (
                <button
                  onClick={() => onGoToCourse && onGoToCourse(course.id)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`,
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: `0 10px 20px -5px ${course.color}40`,
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Go to Course
                </button>
              ) : isVideoCourse && course.readyToBuy === false ? (
                <div style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxSizing: 'border-box'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                  <span>Coming soon</span>
                </div>
              ) : (
                <button
                  onClick={handleBuyCourse}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`,
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: `0 10px 20px -5px ${course.color}40`,
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Buy Now
                </button>
              )}
            </div>

            {/* Course Includes */}
            <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #f1f5f9' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
                This course includes:
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { icon: '🎥', text: `${course.videoCount} on-demand videos` },
                  { icon: '♾️', text: '2 years access' },
                  { icon: '📱', text: 'Access on mobile and PC' },
                  { icon: '🏆', text: 'Certificate of completion' }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#64748b' }}>
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        .mobile-cta-bar {
          display: none;
        }
        
        @media (max-width: 768px) {
          .hero-stats-grid {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          
          .hero-stat-card {
            padding: 10px 14px !important;
            gap: 8px !important;
          }
          
          .hero-stat-card span {
            font-size: 20px !important;
          }
          
          .hero-stat-card > div > div:first-child {
            font-size: 13px !important;
          }
          
          .hero-stat-card > div > div:last-child {
            font-size: 11px !important;
          }
          
          /* Instructor Section Mobile Styles */
          .instructor-title {
            font-size: 1.5rem !important;
            margin-bottom: 20px !important;
          }
          
          .instructor-content {
            gap: 20px !important;
          }
          
          .instructor-avatar {
            width: 80px !important;
            height: 80px !important;
            font-size: 2rem !important;
            border-radius: 16px !important;
          }
          
          .instructor-info {
            min-width: 0 !important;
          }
          
          .instructor-name {
            font-size: 1.25rem !important;
          }
          
          .instructor-role {
            font-size: 14px !important;
            margin-bottom: 12px !important;
          }
          
          .instructor-credentials {
            gap: 10px !important;
            margin-bottom: 16px !important;
          }
          
          .instructor-credentials > div {
            font-size: 13px !important;
          }
          
          .instructor-credentials span:first-child {
            font-size: 16px !important;
          }
          
          .instructor-links a {
            padding: 8px 16px !important;
            font-size: 13px !important;
            flex: 1;
            justify-content: center !important;
          }
          
          .mobile-cta-bar {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border-top: 1px solid rgba(0, 0, 0, 0.1);
            padding: 12px 16px;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
            z-index: 1000;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }
        }
        
        @media (max-width: 1024px) {
          .course-content-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Sticky Mobile CTA Bar */}
      <div className="mobile-cta-bar">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
          {isDiscountValid(course) ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#ef4444'
                }}>
                  {formatVND(course.discountPrice)}
                </span>
                <span style={{
                  fontSize: '0.875rem',
                  color: '#9ca3af',
                  textDecoration: 'line-through'
                }}>
                  {formatVND(course.price)}
                </span>
              </div>
            </>
          ) : (
            <span style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              {formatVND(course.price)}
            </span>
          )}
        </div>

        {hasAccess ? (
          <button
            onClick={() => onGoToCourse && onGoToCourse(course.id)}
            style={{
              padding: '12px 24px',
              background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${course.color}40`,
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            Go to Course
          </button>
        ) : isVideoCourse && course.readyToBuy === false ? (
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            Coming soon
          </div>
        ) : (
          <button
            onClick={handleBuyCourse}
            style={{
              padding: '12px 24px',
              background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${course.color}40`,
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            {user ? 'Buy Now' : 'Sign In'}
          </button>
        )}
      </div>

      {/* Sign In Dialog */}
      <SignInDialog
        isOpen={isSignInDialogOpen}
        onClose={() => setIsSignInDialogOpen(false)}
        onSignIn={handleSignIn}
      />

      {/* Course Registration Modal */}
      {user && (
        <GoogleFormModal
          isOpen={isGoogleFormOpen}
          onClose={() => setIsGoogleFormOpen(false)}
          course={{
            id: course.id,
            name: course.name,
            price: course.price,
            discountPrice: course.discountPrice,
            endDiscountDate: course.endDiscountDate
          }}
          user={{
            name: user.name,
            email: user.email
          }}
        />
      )}
    </div>
  );
};

export default CourseDetail;
